import React from "react";

import queryString from "query-string";
import { connect } from "react-redux";

import { TOGGLE_ON_COMPARE_DROPDOWN } from "app/action_types/website_analysis_action-types";
import { apiHelper } from "app/common/services/apiHelper";
import categoryService from "app/common/services/categoryService";
import { chosenSites as chosenSitesService } from "app/common/services/chosenSites";
import { isAvailable } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { MAX_DOMAINS_IN_CATEGORY } from "app/components/customCategoriesWizard/custom-categories-max-domains";
import { QualificationSideBarHOC } from "app/components/QualificationSideBar/QualificationSideBarHOC";
import { DomainSelection } from "app/components/React/TableSelectionComponents/DomainSelection";
import { AudienceInterestsAPIService } from "app/pages/website-analysis/audience-interests/single/AudienceInterests/AudienceInterestsAPIService";
import { AudienceInterestsOverlapTable } from "app/pages/website-analysis/audience-interests/single/AudienceInterestsOverlapTable";
import { AudienceInterestsTableSettings } from "app/pages/website-analysis/audience-interests/single/AudienceInterestsTableSettings";
import { AudienceInterestsTableTop } from "app/pages/website-analysis/audience-interests/single/AudienceInterestsTableTop/AudienceInterestsTableTop";
import { ReferralsPage } from "app/pages/website-analysis/incoming-traffic/StyledComponents";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { getTableSelectedItems } from "app/reducers/_reducers/tableSelection.reducer";
import CountryService from "app/services/CountryService";
import DurationService from "app/services/DurationService";
import type { RootState } from "single-spa/store/types";

import { tableActionsCreator } from "../../../../../actions/tableActions";
import { showSuccessToast } from "../../../../../actions/toast_actions";
import { SWReactTableWrapperWithSelection } from "../../../../../components/React/Table/SWReactTableWrapperWithSelection";
import { getToastItemComponent } from "../../../../../components/React/Toast/ToastItem";
import { i18nCategoryFilter } from "../../../../../filters/ngFilters";
import { allTrackers } from "../../../../../services/track/track";
import { buildSearchFilterForTable } from "../AudienceInterestsUtils";

import type {
    IAudienceInterestsApiData,
    IAudienceInterestsProps,
    IAudienceInterestsState,
} from "./AudienceInterestsTypes";

declare const similarweb;

const AUDIENCE_TABLE_SELECTION_KEY = "AudienceInterestsTable";

class AudienceInterests extends React.PureComponent<IAudienceInterestsProps, IAudienceInterestsState> {
    private dropdownRef;
    private swNavigator = proNavigator;
    private swSettings: any;

    constructor(props) {
        super(props);
        this.swSettings = swSettings;
        this.dropdownRef = React.createRef();
        this.state = {
            newGroupLoading: false,
            newGroupType: null,
            newGroupError: false,
            newGroupErrorMessage: null,
            allCategories: [],
            customCategories: [],
            topics: [],
        };
    }

    private getSelectedCategoryIds = (): string[] => {
        const { audienceCategory, customCategory } = this.swNavigator.getParams();

        const customCategoryIds = customCategory ? customCategory.split(",") : [];
        const audienceCategoryIds = audienceCategory ? audienceCategory.split(",") : [];
        const allCategoryIds = [...customCategoryIds, ...audienceCategoryIds];

        // The audienceInterestsTableTop expects to have an array
        // with "" in case no id is selected. therefore we
        // check if we have any selected ids
        return allCategoryIds.length > 0 ? allCategoryIds : [""];
    };

    public render() {
        const downloadExcelPermitted = this.swSettings.current.resources.IsExcelAllowed;
        const { search, webSource = "Desktop", duration, country, isWWW } = this.swNavigator.getParams();
        const durationData = DurationService.getDurationData(duration, "", "WebAnalysis");
        const { from, to, isWindow } = durationData.forAPI;
        const chosenSites = chosenSitesService.sitelistForLegend();
        const selectedCategoryIds = this.getSelectedCategoryIds();
        const hasCustomCategoriesPermission = categoryService.hasCustomCategoriesPermission();
        const targetStateForGroupVisit = isAvailable(swSettings.components.WebMarketResearchOverviewHome)
            ? "market_research_overview_v2"
            : "digitalsuite_marketresearch_webmarketanalysis_mapping";

        return (
            <ReferralsPage className="sharedTooltip">
                {chosenSites.length > 1 ? (
                    <AudienceInterestsOverlapTable
                        chosenSites={chosenSites}
                        duration={{ from, to, isWindow }}
                        webSource={webSource}
                        country={country}
                        isWWW={isWWW}
                        getCountryById={this.getCountryById}
                        onOverlapTableSelect={this.onOverlapTableSelect}
                        openCompareModal={this.openCompareModal}
                    />
                ) : null}
                <SWReactTableWrapperWithSelection
                    tableSelectionKey={AUDIENCE_TABLE_SELECTION_KEY}
                    tableSelectionProperty="Domain"
                    maxSelectedRows={MAX_DOMAINS_IN_CATEGORY}
                    cleanOnUnMount={true}
                    serverApi={AudienceInterestsAPIService.getApiEndpoint()}
                    initialFilters={this.getInitialFilters()}
                    tableColumns={this.getColumns(hasCustomCategoriesPermission)}
                    transformData={this.transformData}
                    getDataCallback={this.onGetData}
                    tableOptions={{
                        metric: "MarketingWorkspaceWebsiteGroupTable",
                        aboveHeaderComponents: [
                            <DomainSelection
                                key="DomainSelection"
                                showToast={this.props.showToast}
                                clearAllSelectedRows={this.props.clearAllSelectedRows}
                                selectedRows={this.props.selectedRows}
                                targetStateForGroupVisit={targetStateForGroupVisit}
                            />,
                        ],
                        tableSelectionTrackingParam: "Domain",
                        trackName: "Traffic Sources",
                    }}
                    recordsField="records"
                    totalRecordsField="TotalCount"
                    onSort={this.onSort}
                    preventDefaultOnSort
                    pageIndent={1}
                >
                    {(topComponentProps) => (
                        <AudienceInterestsTableTop
                            {...topComponentProps}
                            duration={{ from, to }}
                            webSource={webSource}
                            country={country}
                            getCountryById={this.getCountryById}
                            searchTerm={search}
                            selectedCategories={selectedCategoryIds}
                            allCategories={this.state.allCategories}
                            customCategories={this.state.customCategories}
                            excelLink={this.getExcel()}
                            downloadExcelPermitted={downloadExcelPermitted}
                            topics={this.state.topics}
                            categoriesData={this.state.categoriesData}
                            domainMetaData={this.state.domainMetaData}
                            domains={chosenSitesService.sitelistForLegend()}
                            selectedDomain={this.getSelectedSite()}
                        />
                    )}
                </SWReactTableWrapperWithSelection>
            </ReferralsPage>
        );
    }

    public getSelectedSite = () => {
        const sites = chosenSitesService.sitelistForLegend();
        const { selectedSite } = this.swNavigator.getParams();
        const selectedSiteItem = sites.find((site) => site.name === selectedSite);
        if (selectedSite && selectedSiteItem) {
            return selectedSiteItem;
        } else {
            return sites[0];
        }
    };

    public getColumns = (useSelection) => {
        const { orderBy, duration } = this.swNavigator.getParams();
        const currentState: any = this.swNavigator.current();
        const handleOpenQualificationSideBar =
            !!currentState.qualificationSideBarComponent && this.props.handleOpenQualificationSideBar;
        let sortedColumn;
        if (orderBy) {
            const [field, sortDirection] = orderBy.split(" ");
            sortedColumn = {
                field,
                sortDirection,
            };
        }
        const columns = AudienceInterestsTableSettings.getColumns(
            sortedColumn,
            duration === "28d",
            handleOpenQualificationSideBar,
            useSelection,
        );
        return columns;
    };

    public getInitialFilters = () => {
        const params = this.swNavigator.getParams();
        const { audienceCategory } = params;
        const categoryObject = categoryService.categoryQueryParamToCategoryObject(audienceCategory);

        params.filter = {};

        // search
        if (params.search) {
            const { filter } = buildSearchFilterForTable("Domain", params.search);
            params.filter = filter;
            delete params.search;
        }
        // orderBy
        if (!params.orderBy) {
            params.orderBy = "RelevancyScore desc";
        }

        // selectedSite should override 'key'
        if (params.selectedSite) {
            params.key = this.getSelectedSite().name;
            delete params.selectedSite;
        }
        const apiParams = apiHelper.transformParamsForAPI(params);

        // category
        if (params.audienceCategory) {
            apiParams.category = params.audienceCategory;
            delete apiParams.audienceCategory;
            params.categoryHash = categoryObject?.categoryHash;
        }

        return apiParams;
    };

    public transformData = (data) => {
        return {
            ...data,
            records: data?.Records?.map((record) => {
                return {
                    ...record,
                    url: this.swNavigator.href("digitalsuite_website_websiteperformance", {
                        ...this.swNavigator.getParams(),
                        key: record.Domain,
                    }),
                    Rank: record.Rank === -1 ? 0 : record.Rank,
                };
            }),
        };
    };

    public onGetData = (data: IAudienceInterestsApiData) => {
        if (!data.Records && data.TotalCount === 0) {
            return;
        }

        const domainName = this.getSelectedSite().name;

        this.setState({
            allCategories: data.Categories[domainName],
            customCategories: data?.CustomCategories ?? [],
            topics: data.Topics,
            categoriesData: similarweb.utils
                .formatTopList(data.Categories[domainName], 6, {
                    transformFunction: similarweb.utils.addCategoryIdFromName,
                })
                .map((cat) => {
                    return {
                        category: i18nCategoryFilter()(cat.Name),
                        categoryApi: cat.Id,
                        ...Object.entries(data.Categories[domainName]).reduce((acc, [domain, domainCategories]) => {
                            return {
                                ...acc,
                                [domainName]: cat.Value,
                            };
                        }, {}),
                    };
                }),
            domainMetaData: [
                {
                    icon: chosenSitesService.listInfo[domainName].icon,
                    name: domainName,
                    color: chosenSitesService.getSiteColor(domainName),
                },
            ],
        });
    };

    public getExcel = () => {
        const params = this.getInitialFilters();
        const domain = this.getSelectedSite().name;
        params.key = domain;
        const queryStringParams = queryString.stringify(params);
        return `/export/analysis/GetAudienceInterestsTsv?${queryStringParams}`;
    };

    public onAdd = (s) => (event) => {};

    public onSort = ({ field, sortDirection }) => {
        this.swNavigator.applyUpdateParams({
            orderBy: `${field} ${sortDirection}`,
        });
    };

    public setStateAsync(newState) {
        return new Promise<void>((resolve, reject) => {
            this.setState(newState, resolve);
        });
    }

    public onListTypeSelect = (typeId) => {
        this.setState({ newGroupType: typeId });
    };

    private getCountryById = (id) => CountryService.getCountryById(id);
    private onOverlapTableSelect = (selectedSite) => {
        this.swNavigator.applyUpdateParams({ selectedSite });
    };
    private openCompareModal = () => {
        allTrackers.trackEvent("drop down", "open", "compare dropdown");
        this.props.toggleCompareButton({ type: TOGGLE_ON_COMPARE_DROPDOWN });
    };
}

const mapStateToProps = (state) => {
    const {
        routing: { params },
    } = state;
    return {
        params,
        selectedRows: getTableSelectedItems(AUDIENCE_TABLE_SELECTION_KEY)(state),
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        clearAllSelectedRows: () => {
            dispatch(tableActionsCreator(AUDIENCE_TABLE_SELECTION_KEY, "Domain").clearAllSelectedRows()); // todo
        },
        showToast: (href, text, label) => {
            dispatch(
                showSuccessToast(
                    getToastItemComponent({
                        text,
                        linkText: label,
                        href,
                        onClick: () =>
                            allTrackers.trackEvent(
                                "add to Custom Category",
                                "click",
                                "internal link/websites.overview",
                            ),
                    }),
                ),
            );
        },
        toggleCompareButton: (action) => {
            dispatch(action);
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps, null, {
    areStatesEqual: (next: RootState, prev: RootState) => {
        const currentPage = next.routing.currentPage;
        const isCurrentPage = "accountreview_website_audience_interests" === currentPage;

        const selectedTableItemsAreIdentical =
            getTableSelectedItems(AUDIENCE_TABLE_SELECTION_KEY)(prev)?.length ===
            getTableSelectedItems(AUDIENCE_TABLE_SELECTION_KEY)(next)?.length;

        if (!isCurrentPage && selectedTableItemsAreIdentical) {
            return true;
        } else {
            return next === prev;
        }
    },
})(QualificationSideBarHOC(AudienceInterests));
